/**
 * Reglas de los archivos: formatos, tamaños y coherencia.
 *
 * Módulo puro, sin dependencias de entorno, para que lo importen igual el
 * navegador —que valida antes de subir, para dar respuesta inmediata— y el
 * servidor, que revalida porque nadie garantiza que la petición venga de
 * nuestra interfaz. Si los límites vivieran en dos lugares, tarde o temprano
 * uno acepta lo que el otro rechaza y el fallo aparece recién al subir.
 */
export const TIPOS_ARCHIVO = [
    'foto',
    'datasheet',
    'certificado',
    'documento',
    'orden_compra',
];
/**
 * Formatos aceptados.
 *
 * SVG queda FUERA a propósito: admite scripts embebidos, y servirlo desde
 * nuestro propio dominio sería un vector de XSS con la misma confianza que el
 * resto de la aplicación.
 */
export const MIME_PERMITIDOS = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/avif',
    'application/pdf',
];
export const EXTENSION = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/avif': 'avif',
    'application/pdf': 'pdf',
};
const MB = 1024 * 1024;
/**
 * Topes por tipo, calibrados contra lo que pesan estos documentos en la
 * práctica industrial: un datasheet generado digitalmente pesa entre 200 KB y
 * 3 MB, pero una ficha escaneada —lo habitual en proveedores chicos— llega a
 * 12 MB porque nadie optimiza el escaneo, y un manual con despieces a 25.
 *
 * El límite no es por costo de almacenamiento sino por quien descarga: un
 * datasheet de 25 MB en la conectividad de una faena es inservible.
 */
export const LIMITE_BYTES = {
    foto: 8 * MB,
    datasheet: 15 * MB,
    certificado: 15 * MB,
    documento: 25 * MB,
    orden_compra: 10 * MB,
};
export const TIPICO_BYTES = {
    foto: 1 * MB,
    datasheet: 3 * MB,
    certificado: 3 * MB,
    documento: 8 * MB,
    orden_compra: 2 * MB,
};
export const ETIQUETA_TIPO = {
    foto: 'Foto',
    datasheet: 'Datasheet / ficha técnica',
    certificado: 'Certificado',
    documento: 'Manual o documento',
    orden_compra: 'Orden de compra',
};
/** Solo las fotos son de catálogo abierto; el resto es documentación reservada. */
export const esPublico = (tipo) => tipo === 'foto';
/**
 * Los tipos que el cliente ve en la ficha del artículo.
 *
 * `foto` no está: la portada ya viaja en el catálogo y una galería no es
 * documentación. `orden_compra` tampoco: cuelga de una orden y es del cliente
 * que la subió, no del artículo.
 */
export const TIPOS_DOCUMENTACION = ['datasheet', 'certificado', 'documento'];
export function esDocumentacion(tipo) {
    return TIPOS_DOCUMENTACION.includes(tipo);
}
/**
 * Si un archivo recién subido se publica al cliente sin que nadie lo revise.
 *
 * La línea se traza por AUTOR del documento, no por utilidad. Un datasheet y un
 * certificado los emite el FABRICANTE, cuya marca el cliente ya ve en la ficha:
 * publicarlos no dice nada que no supiera. `documento` es el cajón —"manual o
 * documento"— y es donde termina lo que escribió el DISTRIBUIDOR: una lista de
 * precios, una hoja con membrete, una cotización. Publicar eso solo rompería la
 * decisión de que el cliente no ve de quién se le compra.
 *
 * Por eso el cajón nace oculto y los otros dos nacen visibles. Lo contrario
 * —todo oculto hasta que alguien apruebe— deja una función que parece
 * construida y no muestra nada: no hay un equipo revisando PDFs uno por uno.
 *
 * Lo que esto NO puede resolver: que el PDF escaneado traiga el membrete
 * adentro. Contra eso está el aviso al proveedor al subir, y que el equipo
 * pueda ocultar cualquier archivo.
 */
export function visiblePorDefecto(tipo) {
    return tipo === 'datasheet' || tipo === 'certificado';
}
/**
 * Cómo se le nombra el documento al cliente.
 *
 * El nombre original NO se le muestra nunca: lo escribió el distribuidor y con
 * frecuencia lleva su razón social ("Ferretería XYZ - ficha 6200.pdf"). Es la
 * misma fuga que la clave del objeto, por otra puerta.
 */
export const ETIQUETA_DOCUMENTACION = {
    datasheet: 'Ficha técnica',
    certificado: 'Certificado',
    documento: 'Manual',
};
/**
 * Convierte un texto en un fragmento seguro para un nombre de archivo.
 *
 * Sin acentos, sin espacios y sin nada que un `Content-Disposition` tenga que
 * escapar. Un nombre con comillas o salto de línea ahí es una cabecera partida.
 */
export function aRanura(texto) {
    return texto
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
}
/**
 * Nombre con el que se descarga: el CÓDIGO del artículo y qué documento es.
 *
 * Se usa el código propio y no el SKU por lo mismo que en la ficha: el SKU
 * identifica una oferta y cambiaría el día que la oferta elegida cambie.
 */
export function nombreDescarga(args) {
    const extension = EXTENSION[args.mime] ?? 'pdf';
    const sufijo = args.indice && args.indice > 1 ? `-${args.indice}` : '';
    return [
        aRanura(args.codigo),
        aRanura(args.nombre),
        aRanura(ETIQUETA_DOCUMENTACION[args.tipo]) + sufijo,
    ]
        .filter(Boolean)
        .join('-')
        .concat(`.${extension}`);
}
export function mimeValido(mime) {
    return MIME_PERMITIDOS.includes(mime);
}
/** Una foto debe ser imagen; el resto de los documentos, PDF. */
export function tipoCoincideConMime(tipo, mime) {
    return tipo === 'foto' ? mime.startsWith('image/') : mime === 'application/pdf';
}
export function formatearBytes(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < MB)
        return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / MB).toFixed(1).replace('.', ',')} MB`;
}
/**
 * Devuelve el motivo del rechazo, o null si el archivo sirve.
 *
 * El mensaje se le muestra a la persona, así que dice qué pasa y qué hacer, no
 * solo que está mal.
 */
export function revisarArchivo(tipo, mime, bytes) {
    if (!mimeValido(mime)) {
        return {
            motivo: `El formato ${mime || '(desconocido)'} no se acepta.`,
            sugerencia: 'Se aceptan JPG, PNG, WebP, AVIF y PDF.',
        };
    }
    if (!tipoCoincideConMime(tipo, mime)) {
        return tipo === 'foto'
            ? { motivo: 'Una foto tiene que ser una imagen.', sugerencia: 'Suba un JPG, PNG o WebP.' }
            : {
                motivo: `${ETIQUETA_TIPO[tipo]} tiene que ser un PDF.`,
                sugerencia: 'Exporte el documento a PDF antes de subirlo.',
            };
    }
    if (bytes <= 0)
        return { motivo: 'El archivo está vacío.' };
    if (bytes > LIMITE_BYTES[tipo]) {
        return {
            motivo: `El archivo pesa ${formatearBytes(bytes)} y el máximo para ` +
                `${ETIQUETA_TIPO[tipo].toLowerCase()} es ${formatearBytes(LIMITE_BYTES[tipo])}.`,
            sugerencia: `Lo habitual son unos ${formatearBytes(TIPICO_BYTES[tipo])}. Comprímalo antes de subirlo.`,
        };
    }
    return null;
}
/**
 * Compone la ruta del objeto en el almacenamiento.
 *
 * La genera SIEMPRE el servidor, con el id del proveedor como primer segmento
 * significativo. Es lo que impide escribir sobre archivos de otro proveedor, y
 * el nombre que manda el cliente se guarda como metadato, nunca como ruta —
 * ahí es donde entrarían los `../` y los nombres con sorpresa.
 */
export function componerClave(args) {
    const extension = EXTENSION[args.mime];
    if (!extension)
        throw new Error(`MIME sin extensión conocida: ${args.mime}`);
    return [
        esPublico(args.tipo) ? 'fotos' : 'documentos',
        args.proveedorId,
        args.productoId ?? 'general',
        `${args.uuid}.${extension}`,
    ].join('/');
}
/**
 * Extrae el proveedor de una clave.
 *
 * Se usa para VERIFICAR, no para confiar: el llamador compara el resultado
 * contra el alcance de quien pide. En la v1 esta extracción se usaba como si
 * la clave fuera de fiar —el comentario decía "la generó el servidor al
 * firmar"— pero la clave llegaba en el cuerpo de la petición, así que cualquier
 * usuario autenticado podía apuntarla al archivo de otro. Como la rama de
 * error borraba el objeto en esa clave, era un borrado arbitrario.
 */
export function proveedorDeClave(clave) {
    const partes = clave.split('/');
    if (partes.length < 4)
        return null;
    if (partes[0] !== 'fotos' && partes[0] !== 'documentos')
        return null;
    return partes[1] || null;
}
/**
 * Firmas de archivo (magic bytes) de los formatos que se aceptan.
 *
 * Hacen falta porque el `Content-Type` que el almacén reporta es el que el
 * cliente DECLARÓ al firmar la subida —la firma de AWS lo fija, así que no
 * puede cambiarlo después, pero sí pudo declararlo mal desde el principio—. Los
 * bytes iniciales del archivo, en cambio, son lo que de verdad hay.
 *
 * Sin esta comprobación, alguien puede declarar `image/png` y subir cualquier
 * cosa: el catálogo quedaría con fichas cuya foto no se puede mostrar, y un
 * datasheet podría no ser un PDF.
 */
const FIRMAS = [
    { mime: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
    { mime: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
    { mime: 'application/pdf', bytes: [0x25, 0x50, 0x44, 0x46] }, // %PDF
    // WebP y AVIF son contenedores: "RIFF"…"WEBP" y "ftyp"…"avif".
    { mime: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46] },
    { mime: 'image/avif', bytes: [0x66, 0x74, 0x79, 0x70], desplazamiento: 4 },
];
/** Cuántos bytes hay que leer del principio para decidir. */
export const BYTES_PARA_FIRMA = 16;
/**
 * Comprueba que los bytes iniciales corresponden al MIME declarado.
 *
 * Devuelve null si calzan, o el motivo del rechazo. Con menos de
 * `BYTES_PARA_FIRMA` bytes disponibles se acepta: un archivo tan pequeño no es
 * un formato válido de todos modos y el límite de tamaño mínimo ya lo rechaza.
 */
export function firmaCoincide(mime, inicio) {
    const candidatas = FIRMAS.filter((f) => f.mime === mime);
    if (candidatas.length === 0)
        return null; // MIME no comprobable: ya lo filtró la lista blanca
    if (inicio.length < 4)
        return null;
    for (const f of candidatas) {
        const off = f.desplazamiento ?? 0;
        if (inicio.length < off + f.bytes.length)
            continue;
        let calza = true;
        for (let i = 0; i < f.bytes.length; i++) {
            if (inicio[off + i] !== f.bytes[i]) {
                calza = false;
                break;
            }
        }
        if (calza) {
            // WebP exige además "WEBP" en el byte 8.
            if (mime === 'image/webp') {
                const marca = [0x57, 0x45, 0x42, 0x50]; // WEBP
                if (inicio.length < 12)
                    return null;
                for (let i = 0; i < 4; i++) {
                    if (inicio[8 + i] !== marca[i]) {
                        return 'El archivo dice ser WebP pero su contenido no lo es.';
                    }
                }
            }
            return null;
        }
    }
    return `El contenido del archivo no corresponde a ${mime}. Compruebe que no lo renombró.`;
}
//# sourceMappingURL=archivos.js.map